import {
  Button,
  Divider,
  Form,
  Input,
  Typography,
  message as messages
} from "antd";
import { AppLogoIcon } from "components/svg-icons";
import { useTokenValidation } from "components/TokenValidationGate";
import FormItem from "antd/es/form/FormItem";
import { useEffect, useState } from "react";
import { SetupAccountRequest } from "models/user";
import { setupAccount } from "services/auth";
import { useHistory } from "react-router";
import { ErrorMessages, Regex } from "constants/index";
import PasswordPolicy, {
  StandardPasswordPolicyAlert,
  PasswordFailedConditionsAlert
} from "components/widgets/auth-widgets/password-policy";

function SetupAccount() {
  const [form] = Form.useForm();
  const { Title } = Typography;
  const [loader, setLoader] = useState(false);
  const history = useHistory();

  const [readonlyPhone, setReadOnlyForm] = useState(true);
  const [passwordHasConditionsError, setPasswordHasConditionsError] =
    useState(false);

  const tokenValidation = useTokenValidation();
  const accessToken = tokenValidation?.token || "";
  const validationStatus = tokenValidation?.validationStatus;
  const decodedToken = tokenValidation?.decodedToken;
  const isEnvAdmin = Boolean(tokenValidation?.isEnvAdminFromToken);
  const isSessionExpired = Boolean(
    tokenValidation?.isSetupAccountSessionExpired
  );

  const goToLoginComponent = (
    <div>
      Password is successfully set.{" "}
      <span>
        <a href={isEnvAdmin ? "/admin/#/" : "/"} className="underline">
          Click here
        </a>
      </span>{" "}
      to Login.
    </div>
  );

  useEffect(() => {
    if (validationStatus !== "USER_ACCOUNT_SETUP_NOT_DONE" || !decodedToken) {
      return;
    }

    form.setFieldsValue({
      first_name: decodedToken["first-name"],
      last_name: decodedToken["last-name"]
    });
  }, [validationStatus, decodedToken, form]);

  useEffect(() => {
    if (!readonlyPhone) return undefined;
    const timeoutId = setTimeout(() => {
      setReadOnlyForm(false);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [readonlyPhone]);

  const onFinish = async (event: SetupAccountRequest) => {
    setLoader(true);

    const result: any = await setupAccount({
      password: event.password,
      token: accessToken,
      first_name: event.first_name,
      last_name: event.last_name,
      phone: event.phone
    });

    setLoader(false);
    if (result.status >= 200 && result.status <= 204) {
      messages.success({
        content: goToLoginComponent,
        duration: 60
      });
    } else if (result.response?.data?.error) {
      const erroMsg =
        result.response?.data?.error === "Link has expired."
          ? ErrorMessages.sessionExpiredMsg
          : result.response?.data?.error;
      messages.error(erroMsg);
      if (result.response?.data?.error === "Link has expired.") {
        if (isEnvAdmin) history.push("/admin/#/");
        else history.push("/");
      }
    } else {
      messages.error(result.message);
    }

    form.resetFields();
  };

  const onFinishFailed = () => {
    const passwordFieldErrors = form.getFieldError("password");
    if (passwordFieldErrors.length > 0) {
      setPasswordHasConditionsError(true);
    } else {
      setPasswordHasConditionsError(false);
    }
  };

  const onValuesChange = () => {
    form.validateFields(["password"]).catch((err) => {
      if (err.errorFields.length > 0 && err.values.password) {
        setPasswordHasConditionsError(true);
      } else {
        setPasswordHasConditionsError(false);
      }
    });
  };

  return (
    <div className="flex min-w-full min-h-screen bg-ciq-neutral-50 items-center justify-center p-2">
      <div className="flex flex-col p-8 shadow-[0px_5px_25px_rgba(0,0,0,0.1)] w-[400px] rounded-md">
        <div className="m-auto block">
          <AppLogoIcon />
        </div>
        {!isSessionExpired && (
          <div>
            <div className="flex justify-center">
              <Title level={3}>Create Account</Title>
            </div>
            <div>
              {passwordHasConditionsError && (
                <PasswordFailedConditionsAlert className="mt-1 mb-2" />
              )}
            </div>
            <div>
              <Form
                onFinish={onFinish}
                className="space-y-3"
                form={form}
                scrollToFirstError
                layout="vertical"
                autoComplete="off"
                onFinishFailed={onFinishFailed}
                onValuesChange={onValuesChange}
              >
                <FormItem
                  name="first_name"
                  label="First Name"
                  rules={[
                    {
                      required: true,
                      validateTrigger: "onSubmit",
                      message: ErrorMessages.FirstName
                    }
                  ]}
                >
                  <Input placeholder="Enter First Name" />
                </FormItem>
                <FormItem
                  name="last_name"
                  label="Last Name"
                  rules={[
                    {
                      required: true,
                      validateTrigger: "onSubmit",
                      message: ErrorMessages.LastName
                    }
                  ]}
                >
                  <Input placeholder="Enter Last Name" />
                </FormItem>
                <FormItem
                  name="phone"
                  label="Phone Number"
                  rules={[
                    {
                      message: ErrorMessages.PhoneNumber,
                      validateTrigger: "onSubmit",
                      pattern: Regex.phoneNumber
                    }
                  ]}
                >
                  <Input
                    placeholder="Enter Phone Number"
                    readOnly={readonlyPhone}
                    autoComplete="off"
                  />
                </FormItem>
                <Divider />
                <FormItem
                  name="password"
                  label="New Password"
                  validateStatus=""
                  className="hide-ant-field-error-msg"
                  rules={[
                    {
                      validateTrigger: "onSubmit",
                      required: true,
                      pattern: Regex.password,
                      message: ErrorMessages.PasswordRequired
                    }
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    placeholder="Enter New Password"
                    autoComplete="new-password"
                  />
                </FormItem>
                <FormItem
                  name="confirmpassword"
                  label="Confirm New Password"
                  dependencies={["password"]}
                  hasFeedback
                  validateStatus=""
                  rules={[
                    {
                      required: true,
                      validateTrigger: "onSubmit",
                      message: ErrorMessages.ConfirmPasswordRequired
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(ErrorMessages.ConfirmPasswordRequired)
                        );
                      }
                    })
                  ]}
                >
                  <Input.Password
                    placeholder="Enter New Password"
                    autoComplete="new-password"
                  />
                </FormItem>
                <div>
                  <PasswordPolicy />
                  <StandardPasswordPolicyAlert />
                </div>
                <div>
                  <Button
                    htmlType="submit"
                    size="middle"
                    style={{ margin: "auto", display: "block" }}
                    loading={loader}
                  >
                    Submit
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default SetupAccount;
